import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CloudSun, Map, Route, UsersRound, Waves } from "lucide-react";

const TAB_CONFIG = [
  { key: "feed", iconComponent: Waves },
  {
    key: "fishing_today",
    iconComponent: CloudSun,
    route: "/fishing-today",
    labelKey: "fishing_today_mobile_nav",
    defaultLabel: "Outlook",
    accessibleLabelKey: "fishing_today_nav",
    accessibleDefaultLabel: "Fishing Outlook",
  },
  { key: "map", iconComponent: Map },
  { key: "groups", iconComponent: UsersRound },
  {
    key: "my_trips",
    iconComponent: Route,
    route: "/trips",
    defaultLabel: "My Trips",
  },
];
const KEYBOARD_OPEN_CLASS = "rp-keyboard-open";

function isEditableField(element) {
  if (!element || element === document.body) {
    return false;
  }

  const tagName = element.tagName?.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    element.isContentEditable
  );
}

function getBottomNavActiveKey(pathname, activeTab) {
  if (pathname === "/feed") return "feed";
  if (pathname === "/fishing-today") return "fishing_today";
  if (pathname === "/map" || pathname.startsWith("/map/")) return "map";
  if (pathname === "/groups" || pathname.startsWith("/groups/")) return "groups";
  if (
    pathname === "/trips" ||
    pathname.startsWith("/trips/") ||
    pathname === "/my-trips" ||
    pathname.startsWith("/my-trips/")
  ) {
    return "my_trips";
  }
  if (pathname !== "/") return "";
  return ["feed", "map", "groups"].includes(activeTab) ? activeTab : "feed";
}

export default function FeedMobileBottomNav({
  activeTab,
  onTabSelect,
  onRouteNavigate,
  canAccessFishingOutlook = false,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const activeKey = getBottomNavActiveKey(location.pathname, activeTab);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const mobileQuery = window.matchMedia?.("(max-width: 768px)");
    const isMobile = () => (mobileQuery ? mobileQuery.matches : window.innerWidth <= 768);
    const setKeyboardClass = (isOpen) => {
      document.body.classList.toggle(KEYBOARD_OPEN_CLASS, Boolean(isOpen));
    };
    const syncKeyboardState = () => {
      const activeElement = document.activeElement;
      setKeyboardClass(isMobile() && isEditableField(activeElement));
    };
    const handleFocusIn = (event) => {
      setKeyboardClass(isMobile() && isEditableField(event.target));
    };
    const handleFocusOut = () => {
      window.setTimeout(syncKeyboardState, 0);
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);
    window.visualViewport?.addEventListener("resize", syncKeyboardState);
    window.addEventListener("resize", syncKeyboardState);
    mobileQuery?.addEventListener?.("change", syncKeyboardState);
    syncKeyboardState();

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      window.visualViewport?.removeEventListener("resize", syncKeyboardState);
      window.removeEventListener("resize", syncKeyboardState);
      mobileQuery?.removeEventListener?.("change", syncKeyboardState);
      setKeyboardClass(false);
    };
  }, []);

  return (
    <nav className="rp-bottomnav" aria-label={t("feed", { defaultValue: "Feed" })}>
      {TAB_CONFIG.filter(
        (tab) => tab.key !== "fishing_today" || canAccessFishingOutlook
      ).map((tab) => {
        const Icon = tab.iconComponent;
        const label = t(tab.labelKey || tab.key, {
          defaultValue: tab.defaultLabel || tab.key,
        });
        const accessibleLabel = t(tab.accessibleLabelKey || tab.labelKey || tab.key, {
          defaultValue: tab.accessibleDefaultLabel || tab.defaultLabel || tab.key,
        });
        const isActive = activeKey === tab.key;
        const handleClick = () => {
          if (tab.route) {
            onRouteNavigate?.(tab.route);
            return;
          }

          onTabSelect?.(tab.key);
        };

        return (
          <button
            key={tab.key}
            type="button"
            className={`rp-bottomnav__tab${
              isActive ? " rp-bottomnav__tab--active" : ""
            }`}
            title={accessibleLabel}
            aria-label={accessibleLabel}
            aria-current={isActive ? "page" : undefined}
            onClick={handleClick}
          >
            <span className="rp-bottomnav__icon-wrap">
              <span className="rp-bottomnav__icon" aria-hidden="true">
                <Icon size={20} strokeWidth={2.1} />
              </span>
            </span>
            <span className="rp-bottomnav__label">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}
